import { createI18n } from "vue-i18n"
import { Preferences } from "@capacitor/preferences"
import { setDisplayLocale } from "@/utils/numberHelpers"

// Import language files
import en from "./locales/en.json"
import nl from "./locales/nl.json"

const messages = {
    en,
    nl,
}

// Function to get system language
const getSystemLanguage = (): any => {
    const systemLang = navigator.language.split("-")[0] // Get language code without region
    return ["en", "nl"].includes(systemLang) ? systemLang : "en"
}

// Function to read a ?locale= / ?lang= override from the URL. Handy on web for
// testing translations without a UI switcher; ignored when absent/invalid.
const getUrlLanguage = (): string | null => {
    try {
        const params = new URLSearchParams(window.location.search)
        const lang = params.get("locale") ?? params.get("lang")
        return lang && ["en", "nl"].includes(lang) ? lang : null
    } catch {
        return null
    }
}

// Function to get stored language or system default
const getStoredLanguage = async (): Promise<any> => {
    // A ?locale=nl / ?lang=nl URL param wins and is persisted so it sticks.
    const urlLang = getUrlLanguage()
    if (urlLang) {
        await setStoredLanguage(urlLang)
        return urlLang
    }

    try {
        const { value } = await Preferences.get({ key: "system_lang" })
        if (value && ["en", "nl"].includes(value)) {
            return value
        }
    } catch (error) {
        console.warn("Could not retrieve stored language:", error)
    }

    // Fallback to system language or default to English
    const systemLang = getSystemLanguage()
    await setStoredLanguage(systemLang)
    return systemLang
}

// Function to store language preference
const setStoredLanguage = async (lang: any): Promise<void> => {
    try {
        await Preferences.set({ key: "system_lang", value: lang })
    } catch (error) {
        console.warn("Could not store language preference:", error)
    }
}

// Create i18n instance with default locale (will be updated after storage check)
const i18n = createI18n({
    legacy: false,
    locale: "en", // Default fallback
    fallbackLocale: "en",
    messages,
    globalInjection: true,
})

// Initialize locale from storage
const initializeLocale = async () => {
    const storedLang = await getStoredLanguage()
    i18n.global.locale.value = storedLang
    // Getallen (komma of punt) volgen dezelfde taal; die helper kan de i18n-
    // instantie niet importeren, dus hij krijgt hem hier aangereikt.
    setDisplayLocale(storedLang)
}

export { i18n, initializeLocale, setStoredLanguage, getStoredLanguage }
